// Picture (the price book round, DEC-019): an item's house-style drawing, the one thing every price
// book row, tile and line shares. The drawing comes in two, ink on paper for light and the blueprint
// for dark, and the theme picks one. With no drawing yet it says which of three it is, dashed (waiting):
// one to approve, one being made, or none (the office's to fill). At the small sizes the word is for
// screen readers; from md up it's drawn too. Decorative by default: the name beside it says what it
// is; pass `alt` where the picture stands alone. Reads only --pic-*.
import './Picture.css';

export type PictureState = 'ready' | 'to_approve' | 'making' | 'none';
export interface PictureSrc {
  light: string;
  dark: string;
}
export type PictureSize = 'xs' | 'sm' | 'md' | 'lg' | 'hero' | 'fill' | 'tile';

export interface PictureProps {
  /** The drawing, light and dark; none yet is a state, not an empty box. */
  src?: PictureSrc | null;
  /** ready when there's a drawing, none when there isn't; to_approve and making say so. */
  state?: PictureState;
  /** xs 28 (a triplet), sm 40 (a table row), md 56 (a picker row), lg 120, hero 200; fill and tile a tile's width, 120 or 76 tall. */
  size?: PictureSize;
  /** Only where the picture stands alone (a queue, a sheet); beside a name it's decorative. */
  alt?: string;
}

export const PICTURE_WORDS: Record<Exclude<PictureState, 'ready'>, string> = {
  to_approve: 'Picture to approve',
  making: 'Picture being made',
  none: 'No picture',
};

export function Picture({ src, state, size = 'sm', alt }: PictureProps) {
  const s: PictureState = state ?? (src ? 'ready' : 'none');
  const word = s === 'ready' ? null : PICTURE_WORDS[s];
  const draws = src && s !== 'making' && s !== 'none';
  return (
    <span className="fl-pic" data-size={size} data-state={s}>
      {draws ? (
        <>
          <img className="fl-pic__img" data-mode="light" src={src.light} alt={alt ?? ''} />
          <img className="fl-pic__img" data-mode="dark" src={src.dark} alt={alt ?? ''} />
        </>
      ) : null}
      {word ? <span className="fl-pic__word">{word}</span> : null}
    </span>
  );
}
